<script setup lang="ts">
import { watch } from 'vue';
import { useRoute } from 'vue-router';
import { useLogto } from '@logto/vue';
import AppShell from './components/AppShell.vue';
import { loadCapabilities, markAuthReady, session } from './lib/session.ts';
import { registerTokenGetter } from './lib/auth.ts';
import { runtimeConfig } from './lib/runtime.ts';
import { demoAccounts, takeSwitch } from './lib/demo-accounts.ts';

const route = useRoute();
const { isAuthenticated, isLoading, getAccessToken, signIn } = useLogto();

/*
 * The second half of switching accounts on a demo deployment.
 *
 * The testing panel signs out before signing in again, because Logto will not
 * take one sign-in on top of another. Arriving back, the pending address is
 * read and forgotten in the same breath — so a sign-in that fails leaves
 * somebody on the front page rather than in a loop — and handed to Logto as a
 * hint. It is not carried in the URL: Logto matches a post-logout address
 * against the registered list exactly, and `/?as=…` is not `/`.
 *
 * It does nothing unless this build has testing accounts, which a real
 * deployment does not. See `lib/demo-accounts.ts`.
 */
if (demoAccounts().length) {
  const asked = takeSwitch();
  if (asked && demoAccounts().some((a) => a.email === asked)) {
    void signIn({ redirectUri: `${window.location.origin}/callback`, loginHint: asked });
  }
}

// The API client asks for a token without needing to know about Logto.
registerTokenGetter(() => getAccessToken(runtimeConfig().apiAudience));

/*
 * Records the auth state; it does not act on it.
 *
 * Redirecting from here raced the SDK: the callback page navigates on, and for
 * a moment `isAuthenticated` is still false, so a signed-in person was sent
 * back to the home page. AppShell renders a sign-in prompt instead, which is a
 * decision that cannot be made too early.
 *
 * Nothing runs while the callback page is working: asking for a token during
 * the handshake never resolves, and the page hangs with no request ever
 * leaving the browser.
 */
watch([isLoading, isAuthenticated, () => route.path], async ([loading, authed, path]) => {
  if (loading) return;
  if (String(path).startsWith('/callback')) return;
  markAuthReady(Boolean(authed));
  if (authed && !session.loaded) await loadCapabilities();
}, { immediate: true });
</script>

<template>
  <AppShell>
    <RouterView />
  </AppShell>
</template>
